import { endpointPath } from "src/hooks/useFetch";
import { ToolDefinition } from "../types";

/**
 * `get_run_events` — fetch the lifecycle/event timeline for a single run.
 *
 * Backed by the same endpoints the run-details pages use:
 *   GET /run/:runID/events           (sequencing)
 *   GET /spatialRun/:runID/events    (cyto/spatial)
 *
 * IMPORTANT: We deliberately hit the cached path. The frontend has a
 * "Refresh from Snowflake" button that adds `?forceRefresh` to bypass the
 * cache; that path is expensive and not appropriate for an interactive AI
 * tool. Kate must NEVER set forceRefresh or days on this endpoint. The only
 * optional query param permitted here is `lastN` (a positive integer that
 * caps the response to the last N events) — useful for failure-forensics
 * questions where the tail of the timeline is what matters.
 *
 * Each event is shaped roughly like:
 *   { id, name, timestamp, sensor, cycle, read?, batch? }
 * sorted by timestamp ascending. Kate uses this to answer questions like
 * "what cycle was the run on when it failed?", "when did the wash start?",
 * or "give me the lifecycle timeline for run X".
 */

const MAX_PAYLOAD_CHARS = 32_000;

const RUN_TYPES = ["sequencing", "spatial"] as const;
type RunType = (typeof RUN_TYPES)[number];

export const runEventToolDefinitions: ToolDefinition[] = [
  {
    name: "get_run_events",
    description:
      "Fetch the lifecycle/event timeline for ONE specific run by id. " +
      "Use this for per-run investigation questions like 'when did run X " +
      "fail?', 'what cycle was it on?', 'when did the wash start?', or " +
      "'give me the timeline for run Y'. " +
      "\n\nDo NOT use this for fleet-level or aggregated questions — those " +
      "should still use execute_sql against the Run/SpatialRun tables. " +
      "\n\nThe response is an array of events sorted by timestamp, each " +
      "with `name` (e.g. SequencingRunInitialized, CycleStarted, " +
      "WashStarted, ReadFinished, Failed), `timestamp`, `cycle`, and " +
      "either `read` (sequencing) or `batch` (spatial). " +
      "\n\nOne call per run is enough — do not loop.",
    input_schema: {
      type: "object",
      properties: {
        runID: {
          type: "string",
          description:
            "The run id (matches Run.id / SpatialRun.id from SQL). " +
            "If you only have a run name, look up the id with execute_sql first.",
        },
        runType: {
          type: "string",
          enum: ["sequencing", "spatial"],
          description:
            "Which run table this id lives in. 'sequencing' for Run / " +
            "SequencingRun, 'spatial' for SpatialRun (cyto). Required — " +
            "the backend endpoint differs by type.",
        },
        lastN: {
          type: "number",
          description:
            "Optional. If provided, only return the LAST N events for the run " +
            "(by timestamp). Use this for failure-forensics questions where " +
            "you only care about the tail of the timeline (e.g. lastN=50 to " +
            "see what happened right before a failure). Omit it for full- " +
            "timeline questions. Must be a positive integer.",
        },
      },
      required: ["runID", "runType"],
    },
  },
];

const getProvider = ():
  | (() => Promise<string>)
  | undefined => window.__telemetryAuth?.getAccessTokenSilently;

const clipForModel = (envelope: unknown): unknown => {
  let serialized: string;
  try {
    serialized = JSON.stringify(envelope);
  } catch {
    return { error: "Could not serialize tool result" };
  }
  if (serialized.length <= MAX_PAYLOAD_CHARS) return envelope;
  return {
    note:
      `Result was ${serialized.length} chars; clipped to ${MAX_PAYLOAD_CHARS}. ` +
      "This run has a lot of events. Answer from the prefix you got, or ask " +
      "the user a more specific question (e.g. only the failure-adjacent events).",
    clipped: serialized.slice(0, MAX_PAYLOAD_CHARS),
  };
};

export async function executeGetRunEvents(
  input: Record<string, unknown>,
): Promise<unknown> {
  const runID = typeof input.runID === "string" ? input.runID.trim() : "";
  const runType = typeof input.runType === "string" ? input.runType.trim() : "";

  if (!runID) {
    return { error: "runID is required" };
  }
  if (!RUN_TYPES.includes(runType as RunType)) {
    return {
      error: `runType must be one of: ${RUN_TYPES.join(", ")}`,
    };
  }

  // Optional lastN: must be a positive integer. We reject 0/negative/non-finite
  // explicitly rather than silently dropping the param so Kate gets a clear
  // error rather than an unexpectedly-large response.
  let lastN: number | null = null;
  if (input.lastN !== undefined && input.lastN !== null) {
    const raw = input.lastN;
    if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0 || !Number.isInteger(raw)) {
      return { error: "lastN must be a positive integer" };
    }
    lastN = raw;
  }

  const provider = getProvider();
  if (!provider) return { error: "Auth context unavailable." };

  let token: string;
  try {
    token = await provider();
  } catch (err) {
    return { error: `Could not obtain auth token: ${(err as Error).message}` };
  }

  const prefix = runType === "spatial" ? "spatialRun" : "run";
  const query = lastN !== null ? `?lastN=${lastN}` : "";
  const url = endpointPath(`/${prefix}/${encodeURIComponent(runID)}/events${query}`);

  let res: Response;
  try {
    res = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (err) {
    return { error: `Network error: ${(err as Error).message}` };
  }

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    return { error: `Events endpoint ${res.status}: ${text || res.statusText}` };
  }

  let events: unknown;
  try {
    events = await res.json();
  } catch (err) {
    return { error: `Could not parse events response: ${(err as Error).message}` };
  }

  const count = Array.isArray(events) ? events.length : null;
  return clipForModel({
    runID,
    runType,
    lastN,
    eventCount: count,
    events,
  });
}
